<script setup>
import {reactive, onMounted} from "vue";
import JsonCSV from 'vue-json-csv';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {ToastUtil} from "@/Utils/ToastUtil";

const data = reactive({
  search: '',
  headers: [
    {
      title: 'Client',
      align: 'start',
      sortable: true,
      key: 'client',
    },
    ...
        [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ].map(month => ({title: month, key: month.toLowerCase(), sortable: true})),
    {
      title: 'total',
      sortable: true,
      key: 'total',
    },
  ],
  items: [],
  dataLoading: false,
  loading: false,
  years: [],
  selectedYear: new Date().getFullYear(),
  currentYear: new Date().getFullYear(),
});

const formatNumber = (num) => {
  let numberFormatter = new Intl.NumberFormat();
  return numberFormatter.format(num)
}

const sumField = (key) => {
  if( ! Array.isArray(data.items) ) {
    return 0
  }
    return data.items.reduce((a, b) => a + (Number.parseInt(b[key]) || 0), 0)
}

const listClaimsSummary = () => {
  data.dataLoading = true
  ApiWrapper.post('report-client-claims','list-claims-summary', {year: data.selectedYear})
      .then((response) => {
        if(response.data){
          let tempItems = response.data;
          data.items = tempItems.sort((x, y) => {
            return x.client.localeCompare(y.client);
          });
        }
      })
      .catch(e => {
        ToastUtil.error(e);
      })
      .finally(() =>{
        data.dataLoading = false
      })
}

onMounted(()=>{

  data.years = [0,1,2,3,4,5,6,7,8,9].map(interval => (data.currentYear-interval)) // allow selecting the last 10 years
  listClaimsSummary();
})
</script>

<template>
  <v-container fluid>
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Monthly Volume Report</v-toolbar-title>
        <v-divider class="mx-4" inset vertical></v-divider>
        <JsonCSV
            :data="data.items"
            name="Monthly Volume Report.csv"
            :labels="data.labels"
        >
          <v-icon> mdi-file-download </v-icon>
        </JsonCSV>
        <v-spacer></v-spacer>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>
      </v-toolbar>
      <v-row class="mx-4">
        <v-col
            class="d-flex justify-end"
            cols="3"
            sm="3"
        >
          <v-select
              :items="data.years"
              v-model="data.selectedYear"
              label="Select Year"
              @update:modelValue="data.selectedYear=$event;listClaimsSummary();"
          ></v-select>
        </v-col>
        <v-col
            class="d-flex justify-end"
            cols="3"
            sm="3"
        >
          <v-text-field
              v-model="data.search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
          ></v-text-field>
        </v-col>
      </v-row>
      <v-data-table
          :headers="data.headers"
          :items="data.items"
          :search="data.search"
          class="elevation-1"
          :loading="data.dataLoading"
          loading-text="Loading... Please wait"
      >
        <template v-slot:[`body.prepend`]="{}">
          <tr>
            <th>Totals</th>
            <th>{{ formatNumber(sumField('january'))    }}</th>
            <th>{{ formatNumber(sumField('february'))   }}</th>
            <th>{{ formatNumber(sumField('march'))      }}</th>
            <th>{{ formatNumber(sumField('april'))      }}</th>
            <th>{{ formatNumber(sumField('may'))        }}</th>
            <th>{{ formatNumber(sumField('june'))       }}</th>
            <th>{{ formatNumber(sumField('july'))       }}</th>
            <th>{{ formatNumber(sumField('august'))     }}</th>
            <th>{{ formatNumber(sumField('september'))  }}</th>
            <th>{{ formatNumber(sumField('october'))    }}</th>
            <th>{{ formatNumber(sumField('november'))   }}</th>
            <th>{{ formatNumber(sumField('december'))   }}</th>
            <th>
              {{
                formatNumber(
                    sumField('january')
                    + sumField('february')
                    + sumField('march')
                    + sumField('april')
                    + sumField('may')
                    + sumField('june')
                    + sumField('july')
                    + sumField('august')
                    + sumField('september')
                    + sumField('october')
                    + sumField('november')
                    + sumField('december')
                )
              }}
            </th>
          </tr>
        </template>
        <template v-slot:[`item.client`]="{ item }">
          <RouterLink :to="`/client/${item.id}`">
            {{ item.client }}
          </RouterLink>
        </template>
        <template v-slot:[`item.january`]="{ item }">
          {{ formatNumber(item.january) }}
        </template>
        <template v-slot:[`item.february`]="{ item }">
          {{ formatNumber(item.february) }}
        </template>
        <template v-slot:[`item.march`]="{ item }">
          {{ formatNumber(item.march) }}
        </template>
        <template v-slot:[`item.april`]="{ item }">
          {{ formatNumber(item.april) }}
        </template>
        <template v-slot:[`item.may`]="{ item }">
          {{ formatNumber(item.may) }}
        </template>
        <template v-slot:[`item.june`]="{ item }">
          {{ formatNumber(item.june) }}
        </template>
        <template v-slot:[`item.july`]="{ item }">
          {{ formatNumber(item.july) }}
        </template>
        <template v-slot:[`item.august`]="{ item }">
          {{ formatNumber(item.august) }}
        </template>
        <template v-slot:[`item.september`]="{ item }">
          {{ formatNumber(item.september) }}
        </template>
        <template v-slot:[`item.october`]="{ item }">
          {{ formatNumber(item.october) }}
        </template>
        <template v-slot:[`item.november`]="{ item }">
          {{ formatNumber(item.november) }}
        </template>
        <template v-slot:[`item.december`]="{ item }">
          {{ formatNumber(item.december) }}
        </template>
        <template v-slot:[`item.total`]="{ item }">
          {{ formatNumber(item.total) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
[v-cloak] {
  display: none;
}

.container {
  padding: 5px;
}

th[role="columnheader"] {
  text-transform: capitalize;
}
</style>